import { useRef, useState } from 'react';
import { useSharedTestId } from '../../common/hooks/use-shared-test-id';
import OverlayPanel from '../common/overlay-panel/overlay-panel.component';
import { DomElementAlignment, DomTargetPosition } from '../enums';
import OdinIcon, { OdinIconSize, type OdinIconName } from '../odin-icon/odin-icon.component';
import type { OdinMenuItem } from './odin-icon-menu-item.component';
import OdinIconMenuItem from './odin-icon-menu-item.component';

export interface OdinIconMenuProps {
  menuIconName: OdinIconName;
  menuItems: Array<OdinMenuItem>;
  className?: string;
  onItemClick: (name: string) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

export default function OdinIconMenu({
  menuIconName,
  menuItems,
  className,
  onItemClick,
  onMenuOpen,
  onMenuClose,
}: OdinIconMenuProps) {
  const [menuVisible, setMenuVisible] = useState(false);
  const menuTargetRef = useRef<HTMLDivElement>(null);

  const onMenuClicked = () => {
    setMenuVisible(true);
    onMenuOpen?.();
  };

  const onMenuClosed = () => {
    setMenuVisible(false);
    onMenuClose?.();
  };

  const itemClickHandler = (name: string) => {
    setMenuVisible(false);
    onItemClick(name);
  };

  const renderComponent = () => {
    return (
      <>
        <div ref={menuTargetRef} onClick={onMenuClicked} className={className}>
          <OdinIcon icon={menuIconName} size={OdinIconSize.ExtraSmall} />
        </div>
        <OverlayPanel
          visible={menuVisible}
          target={menuTargetRef.current}
          targetPosition={DomTargetPosition.BottomLeft}
          elementAlignment={DomElementAlignment.TopLeft}
          hidden={onMenuClosed}
          shouldUseCreatePortal={true}
          shouldRemainOnScreen
          shouldCheckZIndex
          className="odin-bg-gray-4 odin-p-1 odin-text-sm odin-font-normal"
        >
          {menuItems.map((menuItem) => (
            <OdinIconMenuItem key={menuItem.name} {...menuItem} onClick={itemClickHandler} />
          ))}
        </OverlayPanel>
      </>
    );
  };

  return useSharedTestId('odin-icon-menu', renderComponent());
}
